import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { sonnerToast as toast } from '@crabs-ai/ds-react/lib/sonner';
import {
  handleTokenRefresh,
  redirectToLogin,
  isSessionExpiredRefreshError,
  shouldRedirectOnTokenRefreshFailure,
} from './token-refresh-handler';
import { ErrorResponse } from '@/shared/types';
import { useTokenStore } from '@/shared/store/token-store';
import { getCookie } from '@/shared/server-actions';
import { shouldAttemptTokenRefresh } from '@/shared/apis/auth/token-refresh-policy';

/**
 * 인증 관련 에러 코드 정의
 * 1003: 인증 정보 만료
 * 1004: 빈 토큰
 * 401: 인증 실패
 * 403: 권한 없음
 * 412: 특정 조건에 의해 데이터 처리를 할 수 없는 경우 / (e.g 중복된 데이터일 경우 status 412 -> isForceConfirm: true 로 처리)
 * @see https://developer.mozilla.org/ko/docs/Web/HTTP/Status
 */
const AUTH_ERROR_CODES = [401];
const DATA_ERROR_CODES = [412];
const PERMISSION_DENIED_CODES = [1024];

export { shouldAttemptTokenRefresh } from '@/shared/apis/auth/token-refresh-policy';

/**
 * 비정상 로그아웃 사유를 구체적으로 구성
 * Slack 알림에서 원인을 빠르게 파악할 수 있도록 error.code, status, url 등을 포함
 */
const buildDetailedReason = (
  base: string,
  error: AxiosError<ErrorResponse>,
): string => {
  const details: string[] = [base];

  if (error.code) details.push(`code: ${error.code}`);
  if (error.response?.status) details.push(`status: ${error.response.status}`);
  if (error.response?.data?.code)
    details.push(`errCode: ${error.response.data.code}`);
  if (error.config?.url) details.push(`url: ${error.config.url}`);

  return details.join(' | ');
};

/**
 * 인증 없이 접근 가능한 URL 목록
 * 인증 실패 시에도 로그인 페이지로 리다이렉트하지 않아야 하는 경로들
 */
export const NO_AUTH_PATHS = [
  '/',
  '/labs',
  '/pricing',
  '/solution',
  '/payment/plan/product',
  '/auth/signin',
  '/auth/signup',
  '/auth/social-signup',
  '/auth/enterprise-signin',
  '/auth/enterprise-signup',
  '/auth/help/pw',
  '/subscribe',
  '/business',
  '/approve',
  // admin iframe 임베드용 프리셋 프리뷰 — 인증 게이트를 두지 않는다(운영자에게 admin 외 web
  // 로그인까지 강요하지 않기 위함). 임베드는 CSP frame-ancestors + postMessage origin
  // allowlist 로 제한된다 (§next.config.js, §DeckPresetPreviewPage).
  '/deck-preset-preview',
  // 파트너 SSO 화면 3종 — 세션 발급 "전" 단계라 401 시 로그인 리다이렉트가 걸리면
  // 인터스티셜이 로그인 페이지로 튕겨 SSO 플로우가 끊긴다 (Task: sso-partner-handoff)
  '/sso/confirm',
  '/sso/pending',
  '/sso/error',
  // 국제판 법적 문서 3종 — 비로그인 방문자도 랜딩 footer 에서 바로 열람해야 하므로
  // 인증 게이트를 두지 않는다 (Task: intl-legal-pages)
  '/legal/terms',
  '/legal/paid-terms',
  '/legal/privacy',
];

/**
 * 서버 응답 없이 실패하는 Axios 에러 코드
 * 이 에러들은 인증 실패가 아니므로 강제 로그아웃하면 안 됨
 */
const NON_SERVER_ERROR_CODES = [
  'ERR_CANCELED', // AbortSignal 기반 취소
  'ECONNABORTED', // XHR onabort (브라우저 네비게이션, 안드로이드 뒤로가기 등)
  'ETIMEDOUT', // 요청 타임아웃 (Axios transitional 설정에 따라)
  'ERR_NETWORK', // XHR onerror (네트워크 단절, CORS, Mixed Content 등)
];

const isNonServerError = (error: AxiosError): boolean => {
  if (axios.isCancel(error)) return true;
  return !!error.code && NON_SERVER_ERROR_CODES.includes(error.code);
};

/**
 * 현재 경로가 WHITE_LIST에 포함되는지 확인
 */
const isWhiteListUrl = (): boolean => {
  if (typeof window === 'undefined') return false;

  const currentPath = window.location.pathname;
  const currentPathWithoutLanguage = `/${currentPath.split('/').slice(2).join('/')}`;

  return NO_AUTH_PATHS.includes(currentPathWithoutLanguage);
};

/**
 * Response 성공 핸들러
 */
export const handleResponse = (response: AxiosResponse) => response;

/**
 * Response 에러 핸들러
 * - 에러 응답 처리 및 토큰 갱신
 */
export const handleResponseError = async (
  error: AxiosError<ErrorResponse>,
  axiosClient: AxiosInstance,
) => {
  // 토큰 없이 요청이 취소된 경우 등 — 조용히 reject (overlay도 건너뛰어 원본 보존)
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }

  // BE가 내려준 사용자용 한국어 메시지(response.data.message)를 error.message로 끌어올려
  // 호출부에서 `err.message`만 읽더라도 사용자에게 올바른 안내가 노출되도록 한다.
  // 기존에 `err.response.data.message`를 직접 읽던 코드에는 영향 없음 (data는 그대로 유지).
  if (
    error.response?.data?.message &&
    typeof error.response.data.message === 'string' &&
    error.response.data.message.trim().length > 0
  ) {
    error.message = error.response.data.message;
  }

  const { response, config } = error;
  const isWhiteListed = isWhiteListUrl();

  // 요청 설정(config)이 없는 경우, 처리 불가
  if (!config) {
    console.error('Axios config is undefined. Cannot retry request.');
    return Promise.reject(error);
  }

  // 이미 토큰 갱신 후 재시도된 요청인지 확인
  if (config.isRetryRequest) {
    // 재시도 중 네트워크/취소 에러는 인증 실패가 아니므로 로그아웃하지 않음
    if (isNonServerError(error)) {
      return Promise.reject(error);
    }
    // 재시도 요청의 business 403(코드 없음 포함)은 인증 실패가 아니다.
    // 원본 에러를 그대로 전파하며 switch/session 상태를 변경하지 않는다.
    if (!shouldAttemptTokenRefresh(response?.status, response?.data?.code)) {
      return Promise.reject(error);
    }
    // switch 모드에서는 로그인 이동 없이 에러만 전파 (Operator 세션 유지)
    const isSwitchMode = !!useTokenStore.getState().switchAccessToken;
    if (!isWhiteListed && !isSwitchMode) {
      console.error(
        '토큰 갱신 후 재시도한 요청도 실패했습니다. 로그인 페이지로 이동합니다.',
      );
      await redirectToLogin(
        axiosClient,
        buildDetailedReason('TOKEN_INVALID', error),
      );
    }
    return Promise.reject(error);
  }

  // 응답이 없는 경우 (네트워크 단절, 요청 취소, 타임아웃 등)
  // 서버 응답이 없으므로 인증 실패 여부를 판단할 수 없음 → 로그아웃하지 않고 에러만 전파
  if (!response) {
    return Promise.reject(error);
  }

  const { data, status } = response;

  // 시나리오 1: HTTP 500 에러 처리
  if (status === 500) {
    const traceId =
      error.response?.data?.traceId ??
      error.config?.headers?.get?.('X-Trace-Id');

    // 주기 폴링처럼 caller 가 일시 실패를 자체 흡수하는 요청은 전역 토스트 생략
    if (typeof window !== 'undefined' && !config.silentServerErrorToast) {
      toast.error(
        traceId
          ? `서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요. (traceId: ${traceId})`
          : '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        { position: 'top-center' },
      );
    }
    console.error('서버 오류(500)', { traceId, url: error.config?.url });
    return Promise.reject(error);
  }

  // 시나리오 2: 데이터 관련 에러 코드 (412 등)
  if (DATA_ERROR_CODES.includes(status)) {
    return Promise.reject(error);
  }

  // 시나리오 3: 토큰 관련 에러 코드 (1002~1005) => 토큰 갱신 시도
  if (data?.code && shouldAttemptTokenRefresh(undefined, data.code)) {
    // 명시적인 switch 인증 실패에서만 상태를 정리하므로 호출 전 모드를 캡처
    // 새로고침 시 in-memory가 비어있을 수 있으므로 쿠키도 fallback 체크
    const wasSwitchMode =
      !!useTokenStore.getState().switchAccessToken ||
      !!(await getCookie('s_r_t'));
    try {
      config.isRetryRequest = true;
      return await handleTokenRefresh(config, axiosClient);
    } catch (refreshError) {
      console.error(
        'handleTokenRefresh 실패 (토큰 에러 코드 처리 중):',
        refreshError,
      );
      // switch 모드에서는 Operator 세션을 유지하고 로그인 이동 없이 에러를 전파한다.
      // 네트워크/5xx는 switch 상태도 보존되어 후속 재시도가 가능하다.
      if (
        !isWhiteListed &&
        (!wasSwitchMode || isSessionExpiredRefreshError(refreshError)) &&
        shouldRedirectOnTokenRefreshFailure(refreshError)
      ) {
        // refresh token 자연 만료 → reason 없이 redirect (비정상 로그아웃 아님)
        await redirectToLogin(
          axiosClient,
          isSessionExpiredRefreshError(refreshError)
            ? undefined
            : buildDetailedReason('TOKEN_EXPIRED', error),
        );
      }
      return Promise.reject(refreshError);
    }
  }

  // 시나리오 3.5: 무시할 에러 코드 (1024) - 토큰 갱신 없이 에러 전파
  if (
    AUTH_ERROR_CODES.includes(status) &&
    data?.code &&
    PERMISSION_DENIED_CODES.includes(data.code)
  ) {
    return Promise.reject(error);
  }

  // 시나리오 4: HTTP 401 인증 오류 => 토큰 갱신 시도
  if (!config.isRetryRequest && AUTH_ERROR_CODES.includes(status)) {
    // 명시적인 switch 인증 실패에서만 상태를 정리하므로 호출 전 모드를 캡처
    // 새로고침 시 in-memory가 비어있을 수 있으므로 쿠키도 fallback 체크
    const wasSwitchMode =
      !!useTokenStore.getState().switchAccessToken ||
      !!(await getCookie('s_r_t'));
    try {
      config.isRetryRequest = true;
      return await handleTokenRefresh(config, axiosClient);
    } catch (refreshError) {
      console.error(
        'handleTokenRefresh 실패 (인증 에러 코드 처리 중):',
        refreshError,
      );
      if (
        !isWhiteListed &&
        (!wasSwitchMode || isSessionExpiredRefreshError(refreshError)) &&
        shouldRedirectOnTokenRefreshFailure(refreshError)
      ) {
        await redirectToLogin(
          axiosClient,
          isSessionExpiredRefreshError(refreshError)
            ? undefined
            : buildDetailedReason('AUTH_ERROR', error),
        );
      }
      return Promise.reject(refreshError);
    }
  }

  // 시나리오 5: 재시도 후에도 인증/권한 오류가 발생한 경우
  if (config.isRetryRequest && AUTH_ERROR_CODES.includes(status)) {
    const isSwitchMode = !!useTokenStore.getState().switchAccessToken;
    if (!isWhiteListed && !isSwitchMode) {
      await redirectToLogin(
        axiosClient,
        buildDetailedReason('AUTH_ERROR', error),
      );
    }
    return Promise.reject(error);
  }

  // 위의 어떤 특정 에러 케이스에도 해당하지 않는 경우, 받은 에러를 그대로 전파
  return Promise.reject(error);
};
